<template>
  <SoDialog
    v-model="isFlowDiagramShown"
    :card-title="title"
    :loading="false"
    :max-width="9999"
  >
    <SoFlowDiagram :flow="flowManager.flow?.data"></SoFlowDiagram>
  </SoDialog>
  <div v-bind="$attrs" class="h-100">
    <v-progress-linear
      color="primary"
      :model-value="flowManager.currentProgressPercentage"
    />
    <v-card
      v-if="flowManager.flow?.data"
      :loading="flowManager.flow.isLoading"
      class="h-100 overflow-y-auto"
    >
      <v-toolbar class="px-4 py-2" density="compact" color="surface-dim">
        <h2 class="flow__title text-h4 ml-4" data-testid="so-workflow-title">
          {{ flowManager.currentStep?.title || title }}
        </h2>
        <v-spacer></v-spacer>
        <SoButton
          v-if="!isStartingStep"
          :disabled="isFirstStep"
          type="icon"
          icon="arrow-left"
          @click="goBack()"
        />
        <SoButton
          :disabled="!flowManager.canRevisitNext"
          type="icon"
          icon="arrow-right"
          @click="flowManager.revisitNext()"
        />
        <SoButton type="icon" icon="help" @click="isFlowDiagramShown = true" />
        <SoButton type="icon" icon="close" @click="$emit('closed')" />
      </v-toolbar>
      <SoRow :class="!isMobile ? 'flex-nowrap' : 'flex-column'">
        <SoCol>
          <v-window
            :class="{ 'pa-8 pt-3': !isMobile, 'px-8 pt-3': isMobile }"
            :model-value="currentStepIndex"
          >
            <v-window-item
              v-for="(step, index) in flowManager.flow.data.steps"
              :key="index"
            >
              <SoStep
                :step="step"
                :values="flowManager.values"
                :is-action-pending="isActionPending"
                :action-error="flowManager.actionError"
                :is-starting-step="isStartingStep"
                @input="valueChanged"
                @back-clicked="goBack()"
                @action-submitted="performAction"
              />
            </v-window-item>
          </v-window>
        </SoCol>
        <SoCol
          v-if="flowManager.flow.data.helpTitle && !hideHelpText"
          :class="{
            'px-10': isMobile,
            'flow__help-container mt-6 mr-6': !isMobile,
          }"
        >
          <SoExpansionPanels>
            <SoExpansionPanel bg-color="surface-dim">
              <template #title>
                <div class="px-2 py-4 d-flex align-center">
                  <SoIcon size="small" icon="help-circle" />
                  <span class="text-body-1 font-weight-medium ml-3">{{
                    flowManager.flow.data.helpTitle
                  }}</span>
                </div>
              </template>
              <div class="px-2 pb-3">
                <component
                  :is="flowManager.flow.data.helpContent"
                  data-testid="flow-help-content"
                  :current-step="flowManager.currentStep"
                />
              </div>
            </SoExpansionPanel>
          </SoExpansionPanels>
        </SoCol>
      </SoRow>
    </v-card>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType, provide, reactive, watch } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoExpansionPanel from "@/components/expansionPanels/SoExpansionPanel.vue"
  import SoExpansionPanels from "@/components/expansionPanels/SoExpansionPanels.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoStep from "./SoStep.vue"
  import SoDialog from "../components/SoDialog.vue"
  import SoFlowDiagram from "./asDiagram/SoFlowDiagram.vue"
  import { useFlowManager } from "./useFlowManager"
  import { FlowType } from "./useCases"
  import { IStepAction } from "./model/flow.types"
  import { IFlowValues } from "./model/flowValues.types"
  import TrackingService from "@/services/TrackingService"

  export default defineComponent({
    components: {
      SoFlowDiagram,
      SoDialog,
      SoStep,
      SoButton,
      SoExpansionPanel,
      SoExpansionPanels,
      SoRow,
      SoCol,
      SoIcon,
    },
    props: {
      workflowId: {
        type: String as PropType<FlowType>,
        required: true,
      },
      accountNumber: {
        type: String,
        required: true,
      },
      title: {
        type: String,
        default: "",
        required: false,
      },
      startStep: {
        type: String,
        required: false,
        default: "",
      },
    },
    emits: ["completed", "closed"],
    // eslint-disable-next-line vue/component-api-style
    setup(props) {
      const flowManager = reactive(useFlowManager(props.startStep))

      // Watches the two props explicitly rather than using `watchEffect`.
      // `setFlow` used to create queries synchronously, and under
      // `watchEffect` their reactive state became a dependency of the very
      // effect that called it, re-triggering the load (SO-32286).
      watch(
        [() => props.workflowId, () => props.accountNumber],
        ([workflowId, accountNumber]) => {
          if (workflowId && accountNumber) {
            flowManager.setFlow(workflowId)
          }
        },
        { immediate: true },
      )

      provide("flowManager", flowManager)

      return { flowManager }
    },
    data() {
      return {
        isActionPending: {} as Record<string, boolean>,
        isFlowDiagramShown: false,
      }
    },
    computed: {
      isMobile() {
        return this.$vuetify.display.mobile
      },
      currentStepIndex() {
        return this.flowManager.flow?.data.steps.findIndex(
          (step) => step.id === this.flowManager.currentStep?.id,
        )
      },
      isStartingStep() {
        return this.currentStep?.id === this.startStep
      },
      isFirstStep() {
        return this.currentStepIndex === 0
      },
      currentStep() {
        return this.flowManager.flow?.data.steps.find(
          (step) => step.id === this.flowManager.currentStep?.id,
        )
      },
      hideHelpText() {
        return (
          typeof this.currentStepIndex === "number" &&
          this.flowManager.flow?.data.steps[this.currentStepIndex].hideHelpText
        )
      },
    },
    watch: {
      "currentStep.id": function (stepId) {
        stepId &&
          TrackingService.sendEvent(`${this.workflowId}:${stepId}:loaded`)
      },
    },
    methods: {
      async performAction(event: { action: IStepAction; actionIndex: number }) {
        this.isActionPending[event.actionIndex] = true
        try {
          TrackingService.sendEvent(
            `${this.workflowId}:${this.currentStep?.id}:${event.action?.buttonLabel}`,
          )
          const nextStep = await this.flowManager.performAction(event.action)
          if (!nextStep) this.$emit("completed")
        } finally {
          this.isActionPending[event.actionIndex] = false
        }
      },
      valueChanged(event: { key: string; value: unknown }) {
        this.flowManager.values[event.key] = event.value as IFlowValues
      },
      goBack() {
        if (this.isFirstStep) {
          this.$emit("closed")
        } else {
          this.flowManager.previous()
        }
      },
    },
  })
</script>

<style scoped>
  .flow__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .flow__help-container {
    flex: 0 0 360px;
  }
</style>
